import React, { memo } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import IconProvider from '../ui/IconProvider';
import { getCachedDialogs } from '@/lib/stores/dialogStore';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { getStatusColor } from '@/lib/utils/statusColors';
import { useNavigate } from 'react-router-dom';
import CardBase from '../cards/CardBase';

interface DialogNodeData {
  itemId: string;
  itemType: 'dialog';
  label: string;
  expanded?: boolean;
  onToggleExpand?: () => void;
  [key: string]: any;
}

export const DialogNodeExpanded = memo(({ data, selected }: NodeProps) => {
  const { selectedGame } = useSimplifiedUserData();
  const navigate = useNavigate();
  const nodeData = data as DialogNodeData;
  
  // Get the actual dialog data
  const dialogs = selectedGame?.id ? getCachedDialogs(selectedGame.id) : [];
  const dialog = dialogs.find(dlg => dlg.id === nodeData.itemId);
  
  // Get status color
  const statusColor = dialog?.status ? getStatusColor(dialog.status, 'dialogs', selectedGame?.id) : undefined;
  
  // Count sections
  const sections = dialog?.data?.sections || [];
  const sectionCount = sections.length;
  
  const handleOpenInTab = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (dialog?.id) {
      navigate(`/dialogs/${dialog.id}`);
    }
  };
  
  const handleToggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    nodeData.onToggleExpand?.();
  };
  
  return (
    <CardBase
    shadowType="black"
    shadowColor={"#000000"}
    backgroundColor={"#ffffff"}
    border={true}
    borderColor={"#000000"}
    className="min-w-[200px] max-w-[300px]"
    >
      {/* Connection Handles */}
      <Handle 
        type="target" 
        position={Position.Top}
        id="top"
        className="w-3 h-3 !bg-primary"
      />
      <Handle 
        type="source" 
        position={Position.Bottom}
        id="bottom"
        className="w-3 h-3 !bg-primary"
      />
      <Handle 
        type="target" 
        position={Position.Left}
        id="left"
        className="w-3 h-3 !bg-primary"
      />
      <Handle 
        type="source" 
        position={Position.Right}
        id="right"
        className="w-3 h-3 !bg-primary"
      />
      
      {/* Header with icon and actions */}
      <div 
        className="flex items-center gap-2 px-3 py-2 rounded-t-md bg-gradient-to-r from-purple-500 to-pink-500"
      >
        <div className="flex items-center justify-center w-6 h-6 rounded bg-white bg-opacity-20">
          <IconProvider iconstring="fa-comments" size={14} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white">
            {nodeData.label}
          </div>
        </div>
        <button
          onClick={handleToggleExpand}
          className="w-6 h-6 flex items-center justify-center rounded hover:bg-white hover:bg-opacity-20 transition-colors"
          title="Collapse"
        >
          <IconProvider iconstring="fa-compress" size={12} className="text-white" />
        </button>
        <button
          onClick={handleOpenInTab}
          className="w-6 h-6 flex items-center justify-center rounded hover:bg-white hover:bg-opacity-20 transition-colors"
          title="Open in tab"
        >
          <IconProvider iconstring="fa-external-link-alt" size={12} className="text-white" />
        </button>
      </div>
      
      {/* Expanded Body with detailed information */}
      <div className="px-3 py-3 space-y-2">
        {/* Basic Info Section */}
        <div className="space-y-1">
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Basic Info
          </div>
          
          {dialog?.dialog_key && (
            <div className="flex items-center gap-2 text-xs">
              <IconProvider iconstring="fa-key" size={10} className="text-muted-foreground" />
              <span className="font-medium">Key:</span>
              <span className="font-mono text-muted-foreground">{dialog.dialog_key}</span>
            </div>
          )}
          
          {dialog?.status && (
            <div className="flex items-center gap-2 text-xs">
              <div 
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: statusColor }}
              />
              <span className="font-medium">Status:</span>
              <span style={{ color: statusColor }}>{dialog.status}</span>
            </div>
          )}
          
          <div className="flex items-center gap-2 text-xs">
            <IconProvider iconstring="fa-list" size={10} className="text-muted-foreground" />
            <span className="font-medium">Sections:</span>
            <span className="text-muted-foreground">{sectionCount}</span>
          </div>
        </div>
        
        {/* Tags Section */}
        {dialog?.tags && dialog.tags.length > 0 && (
          <div className="space-y-1">
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Tags
            </div>
            <div className="flex flex-wrap gap-1">
              {dialog.tags.slice(0, 5).map((tag, index) => (
                <span 
                  key={index}
                  className="px-2 py-0.5 text-xs rounded bg-secondary text-secondary-foreground"
                >
                  {tag}
                </span>
              ))}
              {dialog.tags.length > 5 && (
                <span className="px-2 py-0.5 text-xs text-muted-foreground">
                  +{dialog.tags.length - 5} more
                </span>
              )}
            </div>
          </div>
        )}
        
        {/* Description Section */}
        {dialog?.description && (
          <div className="space-y-1">
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Description
            </div>
            <div className="text-xs text-muted-foreground line-clamp-3">
              {dialog.description}
            </div>
          </div>
        )}
        
        {/* Sections Preview with Lines */}
        {sections.length > 0 && (
          <div className="space-y-1">
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Dialogue Content
            </div>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {sections.slice(0, 3).map((section: any, sectionIndex: number) => (
                <div key={sectionIndex} className="p-2 rounded bg-secondary/50 space-y-1">
                  {/* Section name */}
                  <div className="font-medium text-xs">
                    {section.name || `Section ${sectionIndex + 1}`}
                  </div>
                  
                  {/* Preview first few lines */}
                  {section.lines && Array.isArray(section.lines) && section.lines.length > 0 && (
                    <div className="space-y-0.5">
                      {section.lines.slice(0, 2).map((line: any, lineIndex: number) => (
                        <div key={lineIndex} className="text-xs border-l-2 border-muted pl-2">
                          {line.speaker && (
                            <div className="font-medium text-foreground">
                              {line.speaker}:
                            </div>
                          )}
                          <div className="text-muted-foreground line-clamp-2">
                            {line.text || line.content || '(Empty line)'}
                          </div>
                        </div>
                      ))}
                      {section.lines.length > 2 && (
                        <div className="text-xs text-muted-foreground italic pl-2">
                          +{section.lines.length - 2} more lines
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Line count if no lines to preview */}
                  {(!section.lines || section.lines.length === 0) && (
                    <div className="text-xs text-muted-foreground italic">
                      No lines in this section
                    </div>
                  )}
                </div>
              ))}
              {sections.length > 3 && (
                <div className="text-xs text-muted-foreground italic">
                  +{sections.length - 3} more sections
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </CardBase>
  );
});

DialogNodeExpanded.displayName = 'DialogNodeExpanded';

